// checkliste — der Baustein mit der groessten Handarbeitsersparnis.
//
// Marianns Ratgeber haben 16 bis 18 Checklistenzeilen pro Seite, verteilt auf
// drei bis vier Abschnitte. In Webflow von Hand waeren das ebenso viele
// verschachtelte Elemente je Seite.
//
// Zwei Betriebsarten, weil die Liste zwei Leben hat: am Bildschirm hakt man ab,
// auf Papier nicht. Abhakbar ist Standard, weil die meisten Leute die Seite
// lesen und nicht drucken — aber der gedruckte Zustand bleibt brauchbar, weil
// die Kaestchen auch leer als Kaestchen aussehen.
//
// Der Haken wird bewusst NICHT gespeichert. Eine Checkliste, die sich beim
// naechsten Besuch merkt, was man vor drei Wochen angeklickt hat, waere eine
// Zusage, die dieser Baustein nicht halten kann — der Stand hinge dann am
// Geraet, nicht am Vorgang. Lieber ehrlich fluechtig.
//
// Einstellungen am Wrapper:
//   data-titel      Ueberschrift ueber der Liste
//   data-abhakbar   "true" (Standard) klickbar mit Fortschritt, "false" Druckliste
//   data-spalten    1–2, Standard 1
import { el, leerHinweis, lesSchalter, lesZahl } from '@lib/bausteine/dom';
import { zerlegeGruppen } from '@lib/bausteine/zerlege';
import { zeichneAus } from '@lib/bausteine/auszeichnung';

// Jede Checkliste auf der Seite braucht eigene Feld-IDs, sonst zeigen die
// Label der zweiten Liste auf die Kaestchen der ersten.
let laufendeNummer = 0;

export function checkliste(wurzel: HTMLElement, quelle: string): Node {
  const gruppen = zerlegeGruppen(quelle);

  if (gruppen.length === 0) {
    return leerHinweis('Noch keine Einträge. Eine Zeile je Punkt, Abschnitte mit ## einleiten.');
  }

  const titel = (wurzel.getAttribute('data-titel') ?? '').trim();
  const abhakbar = lesSchalter(wurzel, 'data-abhakbar', true);
  const spalten = lesZahl(wurzel, 'data-spalten', 1, 1, 2);

  const basisId = `eoe-check-${++laufendeNummer}`;
  const gesamt = gruppen.reduce((summe, gruppe) => summe + gruppe.eintraege.length, 0);

  // Der Zaehler ist aria-live, damit auch ohne Blick auf den Bildschirm
  // ankommt, dass ein Haken gewirkt hat.
  const fortschritt = abhakbar
    ? el(
        'p',
        { class: 'eoe-checkliste__fortschritt', 'aria-live': 'polite' },
        `0 von ${gesamt} erledigt`,
      )
    : null;

  const gruppenContainer = el(
    'div',
    { class: 'eoe-checkliste__gruppen', style: `--eoe-spalten: ${spalten}` },
    ...gruppen.map((gruppe, gruppeIndex) =>
      el(
        'div',
        { class: 'eoe-checkliste__gruppe' },
        gruppe.titel && el('h4', { class: 'eoe-checkliste__kategorie' }, gruppe.titel),
        el(
          'ul',
          { class: 'eoe-checkliste__liste' },
          ...gruppe.eintraege.map((eintrag, index) => {
            const feldId = `${basisId}-${gruppeIndex}-${index}`;
            const [text, zusatz] = eintrag.felder;

            const inhalt = [
              zeichneAus(text ?? ''),
              zusatz && el('span', { class: 'eoe-checkliste__zusatz' }, zeichneAus(zusatz)),
            ];

            return el(
              'li',
              { class: 'eoe-checkliste__zeile' },
              abhakbar
                ? el('input', { class: 'eoe-checkliste__box', type: 'checkbox', id: feldId })
                : // Ein leeres Kaestchen zum Ankreuzen auf Papier. Rein
                  // dekorativ, deshalb aus dem Vorlesegeraet genommen.
                  el('span', {
                    class: 'eoe-checkliste__box eoe-checkliste__box--papier',
                    'aria-hidden': 'true',
                  }),
              abhakbar
                ? el('label', { class: 'eoe-checkliste__text', for: feldId }, ...inhalt)
                : el('span', { class: 'eoe-checkliste__text' }, ...inhalt),
            );
          }),
        ),
      ),
    ),
  );

  const bereich = el(
    'section',
    { class: 'eoe-reset eoe-checkliste' },
    titel && el('h3', { class: 'eoe-titel' }, titel),
    fortschritt,
    gruppenContainer,
  );

  // Das Durchstreichen erledigter Zeilen macht das CSS ueber :checked; hier
  // muss nur der Zaehler nachziehen.
  if (fortschritt) {
    bereich.addEventListener('change', () => {
      const angehakt = bereich.querySelectorAll('.eoe-checkliste__box:checked').length;
      fortschritt.textContent = `${angehakt} von ${gesamt} erledigt`;
    });
  }

  return bereich;
}
